import { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";

const SYMBOLS = [
  { id: 1, name: "Blue Spire",     img: "ui_majorfaction_rocket.png", greyImg: "rune5.png" },
  { id: 2, name: "Purple Diamond", img: "inv_bracer_45green.png",     greyImg: "rune3.png" },
  { id: 3, name: "Gold Ring",      img: "inv_pants_leather_10.png",   greyImg: "rune1.png" },
  { id: 4, name: "Red X",          img: "inv_belt_39a.png",           greyImg: "rune4.png" },
  { id: 5, name: "Green Swirl",    img: "inv_pants_cloth_38.png",     greyImg: "rune2.png" },
];

const CHART_POSITIONS = {
  1: { left: "73%", top: "22%" },
  2: { left: "85%", top: "60%" },
  3: { left: "50%", top: "82%" },
  4: { left: "15%", top: "60%" },
  5: { left: "27%", top: "22%" },
};

const DEFAULT_KEYBINDS = { 1: "1", 2: "2", 3: "3", 4: "4", 5: "5" };
const MODIFIER_KEYS = new Set(["Control", "Alt", "Shift", "Meta", "OS", "AltGraph"]);

// Build a stable, human-readable descriptor for a keypress, e.g. "Ctrl+Shift+F1".
// Returns null for modifier-only presses so the rebind capture waits for the
// actual key.
//
// Uses `e.code` for digits/letters/numpad so the descriptor stays the same
// regardless of which character Shift produces on the user's keyboard layout
// (Shift+1 stays "Shift+1" instead of "Shift+!" on US, "Shift+\"" on UK, etc.).
function keyDescriptor(e) {
  if (MODIFIER_KEYS.has(e.key)) return null;
  const parts = [];
  if (e.ctrlKey)  parts.push("Ctrl");
  if (e.altKey)   parts.push("Alt");
  if (e.shiftKey) parts.push("Shift");
  if (e.metaKey)  parts.push("Meta");
  const code = e.code || "";
  let key;
  if (/^Digit\d$/.test(code))         key = code.slice(5);     // Digit1 → "1"
  else if (/^Key[A-Z]$/.test(code))   key = code.slice(3);     // KeyA   → "A"
  else if (code.startsWith("Numpad")) key = code;              // Numpad1 stays
  else if (/^F\d+$/.test(code))       key = code;              // F1..F24
  else {
    let k = e.key;
    if (k === " ") k = "Space";
    else if (k.length === 1) k = k.toUpperCase();
    key = k;
  }
  parts.push(key);
  return parts.join("+");
}

function matchesKeybind(e, binding) {
  if (!binding) return false;
  const desc = keyDescriptor(e);
  if (!desc) return false;
  if (desc === binding) return true;
  // Legacy fallback: old bindings stored just `e.key` (no modifiers, no case
  // normalization). Accept a case-insensitive match only when no modifier was
  // pressed and the stored binding has no "+" separators.
  if (!binding.includes("+") && !e.ctrlKey && !e.altKey && !e.shiftKey && !e.metaKey) {
    return binding.toLowerCase() === e.key.toLowerCase();
  }
  return false;
}
const DEFAULT_SETTINGS = {
  stepTimeoutMs: 1500,
  showYouSee: true,
  alertFlash: true,
  alertSound: false,
  phaseGradient: false,
  iconBackdrop: false,
  buttonsPosition: "bottom", // "top" | "bottom" | "left" | "right"
};
const BUTTON_POSITIONS = ["top", "bottom", "left", "right"];
const STATS_STORAGE_KEY = "lura-edicts.roundStats.v1";
const KEYBINDS_STORAGE_KEY = "lura-edicts.keybinds.v1";
const SETTINGS_STORAGE_KEY = "lura-edicts.settings.v1";
const ICON_ORDER_STORAGE_KEY = "lura-edicts.iconOrder.v1";
const DEFAULT_ICON_ORDER = [1, 2, 3, 4, 5];

function loadStoredStats() {
  try {
    const raw = localStorage.getItem(STATS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(r => r && Array.isArray(r.reactions) && typeof r.won === "boolean");
  } catch {
    return [];
  }
}

function loadStoredKeybinds() {
  try {
    const raw = localStorage.getItem(KEYBINDS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_KEYBINDS };
    const parsed = JSON.parse(raw);
    const merged = { ...DEFAULT_KEYBINDS };
    for (const id of [1, 2, 3, 4, 5]) {
      if (typeof parsed[id] === "string" && parsed[id].length > 0) merged[id] = parsed[id];
    }
    return merged;
  } catch {
    return { ...DEFAULT_KEYBINDS };
  }
}

function loadStoredIconOrder() {
  try {
    const raw = localStorage.getItem(ICON_ORDER_STORAGE_KEY);
    if (!raw) return [...DEFAULT_ICON_ORDER];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length !== 5) return [...DEFAULT_ICON_ORDER];
    const sorted = [...parsed].sort((a, b) => a - b);
    if (sorted.join(",") !== DEFAULT_ICON_ORDER.join(",")) return [...DEFAULT_ICON_ORDER];
    return parsed;
  } catch {
    return [...DEFAULT_ICON_ORDER];
  }
}

function loadStoredSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw);
    const merged = { ...DEFAULT_SETTINGS };
    if (typeof parsed.stepTimeoutMs === "number" && parsed.stepTimeoutMs >= 300 && parsed.stepTimeoutMs <= 10000) {
      merged.stepTimeoutMs = parsed.stepTimeoutMs;
    }
    if (typeof parsed.showYouSee === "boolean") merged.showYouSee = parsed.showYouSee;
    if (typeof parsed.alertFlash === "boolean") merged.alertFlash = parsed.alertFlash;
    if (typeof parsed.alertSound === "boolean") merged.alertSound = parsed.alertSound;
    if (typeof parsed.phaseGradient === "boolean") merged.phaseGradient = parsed.phaseGradient;
    if (typeof parsed.iconBackdrop === "boolean") merged.iconBackdrop = parsed.iconBackdrop;
    if (BUTTON_POSITIONS.includes(parsed.buttonsPosition)) merged.buttonsPosition = parsed.buttonsPosition;
    return merged;
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

// Lazy-created AudioContext for short alert beeps.
let _audioCtx = null;
function playBeep() {
  try {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    if (ctx.state === "suspended") ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.2);
  } catch { /* audio unsupported / blocked — ignore */ }
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const PHASE3_LIST_SIZES = [3, 3, 4];

function generatePhase3Round() {
  // Each list is a random subset of distinct symbols of the given size.
  const lists = PHASE3_LIST_SIZES.map(size => shuffle([1, 2, 3, 4, 5]).slice(0, size));
  return { mode: "phase3", lists };
}

function generateRound() {
  const sequence = shuffle([1, 2, 3, 4, 5]);
  return {
    sequence,
    visibility: {
      tank:       [0, 3], // positions 1 and 4
      healerRed:  [1, 4], // positions 2 and 5
      healerBlue: [2],    // position 3
    },
  };
}

function realmLabel(role) {
  if (role === "tank")       return { text: "Tank — main realm",     cls: "tank"   };
  if (role === "healerRed")  return { text: "Healer — red realm",    cls: "red"    };
  if (role === "healerBlue") return { text: "Healer — blue realm",   cls: "blue"   };
  if (role === "phase3")     return { text: "Phase 3 — list challenge", cls: "phase3" };
  return null;
}

export default function App() {
  const [role, setRole] = useState(null);
  const [round, setRound] = useState(null);
  const [step, setStep] = useState(0);
  const [status, setStatus] = useState("idle");
  const [keybinds, setKeybinds] = useState(loadStoredKeybinds);
  const [rebinding, setRebinding] = useState(null);
  const [flashId, setFlashId] = useState(null);
  const [history, setHistory] = useState([]);
  const [lossReason, setLossReason] = useState(null); // "wrong" | "missed"
  const [roundStats, setRoundStats] = useState(loadStoredStats); // [{ won, reactions: [ms] }]
  const [countdown, setCountdown] = useState(null); // 3, 2, 1 then null
  const [showKeybinds, setShowKeybinds] = useState(false);
  const [settings, setSettings] = useState(loadStoredSettings);
  const [iconOrder, setIconOrder] = useState(loadStoredIconOrder);
  const [turnFlash, setTurnFlash] = useState(false);
  const [stepSubmitted, setStepSubmitted] = useState(false);
  const [listIdx, setListIdx] = useState(0); // phase3 only: which list we're on

  const stateRef = useRef();
  stateRef.current = { status, round, step, role, keybinds, rebinding, stepSubmitted, listIdx };

  // Reset the "already submitted" flag at the start of each step and round.
  useEffect(() => { setStepSubmitted(false); }, [step, listIdx, round]);

  // When the current step opens, mark its start time so we can measure reaction.
  const stepStartRef = useRef(0);
  useEffect(() => {
    if (status === "playing") stepStartRef.current = performance.now();
  }, [status, step, listIdx]);

  const submit = useCallback((symbolId, source) => {
    const s = stateRef.current;
    if (s.status !== "playing" || !s.round) return;
    if (source === "you" && s.stepSubmitted) return; // already locked in this step
    const isPhase3 = s.round.mode === "phase3";
    const expected = isPhase3 ? s.round.lists[s.listIdx][s.step] : s.round.sequence[s.step];
    const ok = symbolId === expected;
    const reaction = source === "you" ? Math.round(performance.now() - stepStartRef.current) : null;
    setFlashId(symbolId);
    setTimeout(() => setFlashId(null), 150);
    setHistory(h => [...h, { symbolId, source, ok, position: s.step, listIdx: s.listIdx, reaction }]);
    if (!ok) {
      if (source === "you") { setLossReason("wrong"); setStatus("lost"); }
      return;
    }
    if (source === "auto") {
      // Auto-play already fires at step expiry — advance immediately.
      const next = s.step + 1;
      if (next >= 5) setStatus("won");
      else setStep(next);
      return;
    }
    // Player got it right — lock the tile. The step timer below advances
    // when the full time window has elapsed. Same pacing in edicts and phase 3.
    setStepSubmitted(true);
  }, []);

  // Each tile gets `stepTimeoutMs` to be played. At expiry:
  //   - edicts auto step: fire the correct click + advance
  //   - edicts player step, locked in: advance to next position
  //   - phase 3 (always player): if you haven't clicked yet → miss
  //     (a correct click in phase 3 has already advanced the step before
  //     this timer fires)
  //   - any player step with nothing clicked: miss → lose
  useEffect(() => {
    if (status !== "playing" || !round || !role) return;
    const isPhase3 = round.mode === "phase3";
    const mine = isPhase3 ? null : round.visibility[role];
    const isAuto = !isPhase3 && !mine.includes(step);
    const t = setTimeout(() => {
      if (isAuto) {
        submit(round.sequence[step], "auto");
        return;
      }
      if (stateRef.current.stepSubmitted) {
        if (isPhase3) {
          const currentList = round.lists[listIdx];
          const nextStep = step + 1;
          if (nextStep < currentList.length) {
            setStep(nextStep);
          } else if (listIdx + 1 < round.lists.length) {
            setListIdx(listIdx + 1);
            setStep(0);
          } else {
            setStatus("won");
          }
        } else {
          const next = step + 1;
          if (next >= 5) setStatus("won");
          else setStep(next);
        }
      } else {
        setHistory(h => [...h, { symbolId: null, source: "you", ok: false, position: step, listIdx, missed: true }]);
        setLossReason("missed");
        setStatus("lost");
      }
    }, settings.stepTimeoutMs);
    return () => clearTimeout(t);
  }, [status, round, role, step, listIdx, submit, settings.stepTimeoutMs]);

  useEffect(() => {
    const onKey = (e) => {
      const s = stateRef.current;
      if (s.rebinding !== null) {
        // Allow Esc alone to cancel; Esc + modifiers is captured normally.
        if (e.key === "Escape" && !e.ctrlKey && !e.altKey && !e.shiftKey && !e.metaKey) {
          setRebinding(null);
          return;
        }
        const desc = keyDescriptor(e);
        if (!desc) return; // modifier-only press — keep waiting
        e.preventDefault();
        setKeybinds(prev => ({ ...prev, [s.rebinding]: desc }));
        setRebinding(null);
        return;
      }
      if (s.status !== "playing") return;
      const isPhase3 = s.round && s.round.mode === "phase3";
      if (!isPhase3) {
        const mine = s.round.visibility[s.role];
        if (!mine.includes(s.step)) return;
      }
      const entry = Object.entries(s.keybinds).find(([, k]) => matchesKeybind(e, k));
      if (entry) { e.preventDefault(); submit(Number(entry[0]), "you"); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [submit]);

  const startRound = (chosen) => {
    let actualRole = chosen;
    if (chosen === "healer") actualRole = Math.random() < 0.5 ? "healerRed" : "healerBlue";
    // "healerRed" / "healerBlue" passed in directly stay as-is.
    setRole(actualRole);
    setRound(actualRole === "phase3" ? generatePhase3Round() : generateRound());
    setStep(0);
    setListIdx(0);
    setStatus("countdown");
    setCountdown(3);
    setHistory([]);
    setLossReason(null);
  };

  const resetAll = () => {
    setRole(null); setRound(null); setStep(0); setListIdx(0); setStatus("idle"); setHistory([]); setLossReason(null); setCountdown(null);
  };

  // Persist roundStats across refreshes.
  useEffect(() => {
    try { localStorage.setItem(STATS_STORAGE_KEY, JSON.stringify(roundStats)); }
    catch { /* quota / private mode — fine to drop */ }
  }, [roundStats]);

  // Persist keybinds across refreshes.
  useEffect(() => {
    try { localStorage.setItem(KEYBINDS_STORAGE_KEY, JSON.stringify(keybinds)); }
    catch { /* quota / private mode — fine to drop */ }
  }, [keybinds]);

  // Persist user settings.
  useEffect(() => {
    try { localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings)); }
    catch { /* quota / private mode — fine to drop */ }
  }, [settings]);

  // Persist icon display order.
  useEffect(() => {
    try { localStorage.setItem(ICON_ORDER_STORAGE_KEY, JSON.stringify(iconOrder)); }
    catch { /* quota / private mode — fine to drop */ }
  }, [iconOrder]);

  const moveIcon = useCallback((symbolId, direction) => {
    setIconOrder(prev => {
      const idx = prev.indexOf(symbolId);
      const target = idx + direction;
      if (idx < 0 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }, []);

  const orderedSymbols = iconOrder.map(id => SYMBOLS.find(s => s.id === id)).filter(Boolean);

  // Trigger flash + beep when this step belongs to the player.
  useEffect(() => {
    if (status !== "playing" || !round || !role) return;
    const mine = round.mode === "phase3" ? true : round.visibility[role].includes(step);
    if (!mine) return;
    if (settings.alertFlash) {
      setTurnFlash(true);
      const t = setTimeout(() => setTurnFlash(false), 450);
      if (settings.alertSound) playBeep();
      return () => clearTimeout(t);
    }
    if (settings.alertSound) playBeep();
  }, [status, round, role, step, listIdx, settings.alertFlash, settings.alertSound]);

  // 3-2-1 countdown, then start the round.
  useEffect(() => {
    if (status !== "countdown") return;
    if (countdown === null) return;
    if (countdown === 0) { setStatus("playing"); setCountdown(null); return; }
    const t = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(t);
  }, [status, countdown]);

  const isPhase3 = round && round.mode === "phase3";
  const myPositions = (!isPhase3 && round && role) ? round.visibility[role] : [];
  const isMyTurn = status === "playing" && (isPhase3 || (round && myPositions.includes(step)));
  const label = role && realmLabel(role);
  const phase3CurrentList = isPhase3 ? round.lists[listIdx] : null;
  const phase3TotalSteps = isPhase3 ? round.lists.reduce((a, l) => a + l.length, 0) : 0;
  const phase3DoneSteps = isPhase3 ? (round.lists.slice(0, listIdx).reduce((a, l) => a + l.length, 0) + step) : 0;

  // Push a summary into roundStats exactly once when this round ends.
  const summarizedRef = useRef(null);
  useEffect(() => {
    if (status !== "won" && status !== "lost") return;
    const sig = round
      ? (round.mode === "phase3"
          ? "p3:" + round.lists.map(l => l.join("")).join("|")
          : round.sequence.join(","))
      : "";
    const key = sig + ":" + status;
    if (key === summarizedRef.current) return;
    summarizedRef.current = key;
    const reactions = history.filter(h => h.reaction != null && h.ok).map(h => h.reaction);
    setRoundStats(prev => [...prev, { won: status === "won", reactions, mode: round && round.mode === "phase3" ? "phase3" : "edicts" }]);
  }, [status, history, round]);

  const currentReactions = history.filter(h => h.reaction != null && h.ok).map(h => h.reaction);
  const avg = (arr) => arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : null;
  const fastest = (arr) => arr.length ? Math.min(...arr) : null;
  const slowest = (arr) => arr.length ? Math.max(...arr) : null;
  const allReactions = roundStats.flatMap(r => r.reactions);
  const wins = roundStats.filter(r => r.won).length;

  return (
    <div className={"wrap" + (settings.iconBackdrop ? " icon-backdrop" : "")}>
      <div className="title-row">
        <div>
          <h1>Lura's Edicts — practice</h1>
          <div className="sub">Five symbols, one combination, three players. Pick a role and read your edicts.</div>
        </div>
        <div className="row" style={{gap: 8}}>
          <button className="btn" onClick={resetAll}>⌂ Home</button>
          <button
            className={"btn" + (showKeybinds ? " primary" : "")}
            onClick={() => setShowKeybinds(v => !v)}
          >
            ⚙ Settings
          </button>
        </div>
      </div>

      {showKeybinds && (
        <div className="panel keybinds-panel">
          <div className="row header-row">
            <h3 className="panel-title" style={{margin: 0}}>Settings</h3>
            <button className="btn" onClick={() => setSettings({ ...DEFAULT_SETTINGS })}>Reset settings</button>
          </div>

          <div className="settings-grid">
            <label className="setting-row">
              <span className="setting-label">Step time window</span>
              <span className="setting-control">
                <input
                  type="range"
                  min={500}
                  max={3500}
                  step={50}
                  value={settings.stepTimeoutMs}
                  onChange={e => setSettings(s => ({ ...s, stepTimeoutMs: Number(e.target.value) }))}
                />
                <input
                  type="number"
                  min={300}
                  max={10000}
                  step={50}
                  value={settings.stepTimeoutMs}
                  onChange={e => {
                    const n = Number(e.target.value);
                    if (Number.isFinite(n)) setSettings(s => ({ ...s, stepTimeoutMs: Math.max(300, Math.min(10000, n)) }));
                  }}
                  className="setting-number"
                />
                <span className="setting-unit">ms</span>
              </span>
            </label>

            <label className="setting-row toggle">
              <span className="setting-label">Show "You see" pill</span>
              <input
                type="checkbox"
                checked={settings.showYouSee}
                onChange={e => setSettings(s => ({ ...s, showYouSee: e.target.checked }))}
              />
            </label>

            <label className="setting-row toggle">
              <span className="setting-label">Flash on your turn</span>
              <input
                type="checkbox"
                checked={settings.alertFlash}
                onChange={e => setSettings(s => ({ ...s, alertFlash: e.target.checked }))}
              />
            </label>

            <label className="setting-row toggle">
              <span className="setting-label">Sound on your turn</span>
              <input
                type="checkbox"
                checked={settings.alertSound}
                onChange={e => setSettings(s => ({ ...s, alertSound: e.target.checked }))}
              />
            </label>

            <label className="setting-row toggle">
              <span className="setting-label">Phase-colored panel background (red / blue)</span>
              <input
                type="checkbox"
                checked={settings.phaseGradient}
                onChange={e => setSettings(s => ({ ...s, phaseGradient: e.target.checked }))}
              />
            </label>

            <label className="setting-row toggle">
              <span className="setting-label">In-game icon backdrop (blue-gray glow)</span>
              <input
                type="checkbox"
                checked={settings.iconBackdrop}
                onChange={e => setSettings(s => ({ ...s, iconBackdrop: e.target.checked }))}
              />
            </label>

            <label className="setting-row">
              <span className="setting-label">Selection panel position</span>
              <span className="setting-control">
                {BUTTON_POSITIONS.map(pos => (
                  <button
                    key={pos}
                    className={"btn" + (settings.buttonsPosition === pos ? " primary" : "")}
                    onClick={() => setSettings(s => ({ ...s, buttonsPosition: pos }))}
                    type="button"
                  >
                    {pos === "top" ? "Above" : pos === "bottom" ? "Below" : pos === "left" ? "Left" : "Right"}
                  </button>
                ))}
              </span>
            </label>
          </div>

          <div className="row header-row" style={{marginTop: 20}}>
            <h3 className="panel-title" style={{margin: 0}}>Keybinds &amp; order</h3>
            <div className="row" style={{gap: 8}}>
              <button className="btn" onClick={() => setIconOrder([...DEFAULT_ICON_ORDER])}>Reset order</button>
              <button className="btn" onClick={() => setKeybinds({ ...DEFAULT_KEYBINDS })}>Reset keybinds</button>
            </div>
          </div>
          <div className="keybind-list">
            {orderedSymbols.map((sym, displayIdx) => (
              <div key={sym.id} className="keybind-row">
                <div className="order-controls">
                  <button
                    className="btn order-btn"
                    onClick={() => moveIcon(sym.id, -1)}
                    disabled={displayIdx === 0}
                    title="Move up"
                  >▲</button>
                  <button
                    className="btn order-btn"
                    onClick={() => moveIcon(sym.id, +1)}
                    disabled={displayIdx === orderedSymbols.length - 1}
                    title="Move down"
                  >▼</button>
                </div>
                <img src={sym.img} alt={sym.name} />
                <div className="keybind-meta">
                  <div className="keybind-name">#{sym.id} {sym.name}</div>
                  <div className="keybind-hint">Click Rebind, then press your key — modifiers like Ctrl/Alt/Shift are recorded. Esc cancels.</div>
                </div>
                <span className={"big-key" + (rebinding === sym.id ? " rebinding" : "")}>
                  {rebinding === sym.id ? "press…" : keybinds[sym.id]}
                </span>
                <button
                  className={"btn" + (rebinding === sym.id ? " danger" : "")}
                  onClick={() => setRebinding(rebinding === sym.id ? null : sym.id)}
                >
                  {rebinding === sym.id ? "Cancel" : "Rebind"}
                </button>
              </div>
            ))}
          </div>

          <div className="row" style={{justifyContent: "flex-end", marginTop: 16}}>
            <button className="btn primary" onClick={() => { setRebinding(null); setShowKeybinds(false); }}>
              Save settings
            </button>
          </div>
        </div>
      )}

      {status === "idle" && (
        <div className="panel">
          <h3 className="panel-title">Choose your role</h3>
          <div className="role-buttons">
            <button className="btn primary big" onClick={() => startRound("tank")}>Tank</button>
            <button className="btn primary big" onClick={() => startRound("healer")}>Healer (random phase)</button>
            <button className="btn primary big" onClick={() => startRound("healerRed")}>Red Healer</button>
            <button className="btn primary big" onClick={() => startRound("healerBlue")}>Blue Healer</button>
            <button className="btn primary big" onClick={() => startRound("phase3")}>Phase 3 (list challenge)</button>
          </div>
          <p className="legend">
            <b>Tank</b> stays in the main realm and sees positions 1 and 4.<br/>
            <b>Red Healer</b> always sees positions 2 and 5.<br/>
            <b>Blue Healer</b> always sees position 3.<br/>
            <b>Healer (random phase)</b> picks red or blue for you at the start of each round.<br/>
            <b>Phase 3</b> is solo: three lists of 3, 3, 4 icons — click each list's icons in the order shown.
          </p>
        </div>
      )}

      {status !== "idle" && round && (
        <div className="panel">
          <div className="row header-row">
            <div className="row">
              <span className={"pill " + label.cls}>{label.text}</span>
              {isPhase3 ? (
                <>
                  <span className="pill">List {listIdx + 1} / {round.lists.length}</span>
                  <span className="pill">Step {Math.min(step + 1, phase3CurrentList.length)} / {phase3CurrentList.length}</span>
                  <span className="pill">Total {phase3DoneSteps} / {phase3TotalSteps}</span>
                </>
              ) : (
                <>
                  <span className="pill">Step {Math.min(step + 1, 5)} / 5</span>
                  {settings.showYouSee && (
                    <span className="pill">You see: {myPositions.map(p => p + 1).join(", ")}</span>
                  )}
                </>
              )}
            </div>
            <div className="row">
              <button className="btn" onClick={() => startRound(role)}>New round</button>
            </div>
          </div>

          <div className={`play-area play-area-${settings.buttonsPosition}`}>

          {!isPhase3 && (
            <div className={
              "chart"
              + (turnFlash ? " flashing" : "")
              + (settings.phaseGradient && role === "healerRed" ? " phase-red" : "")
              + (settings.phaseGradient && role === "healerBlue" ? " phase-blue" : "")
            }>
              {status === "countdown" && countdown !== null && (
                <div className="countdown-overlay">
                  <div key={countdown} className="countdown-number">{countdown}</div>
                </div>
              )}
              {status === "playing" && (() => {
                const currentSym = SYMBOLS.find(s => s.id === round.sequence[step]);
                const centerSrc = settings.iconBackdrop ? currentSym.greyImg : currentSym.img;
                return (
                  <div className={"slot center" + (stepSubmitted ? " locked" : "")}>
                    {isMyTurn
                      ? <img src={centerSrc} alt={currentSym.name} />
                      : <span className="qmark">?</span>}
                  </div>
                );
              })()}
              {[0,1,2,3,4].map(posIdx => {
                const posNum = posIdx + 1;
                const symbolId = round.sequence[posIdx];
                const sym = SYMBOLS.find(s => s.id === symbolId);
                const revealed = posIdx < step || status === "won";
                const isCurrent = posIdx === step && status === "playing";
                const cls = ["slot"];
                if (isCurrent) cls.push("current");
                if (revealed)  cls.push("filled");
                return (
                  <div key={posIdx} className={cls.join(" ")} style={CHART_POSITIONS[posNum]}>
                    <span className="pos">{posNum}</span>
                    {revealed
                      ? <img src={sym.img} alt={sym.name} />
                      : <span className="qmark">?</span>}
                  </div>
                );
              })}
            </div>
          )}

          {isPhase3 && (
            <div className={"phase3-area" + (turnFlash ? " flashing" : "")}>
              {status === "countdown" && countdown !== null && (
                <div className="countdown-overlay phase3-countdown">
                  <div key={countdown} className="countdown-number">{countdown}</div>
                </div>
              )}
              {status === "playing" && (() => {
                const currentSym = SYMBOLS.find(s => s.id === phase3CurrentList[step]);
                const centerSrc = currentSym && (settings.iconBackdrop ? currentSym.greyImg : currentSym.img);
                return (
                  <div className={"slot center phase3-center" + (stepSubmitted ? " locked" : "")}>
                    {currentSym && <img src={centerSrc} alt={currentSym.name} />}
                  </div>
                );
              })()}
              <div className="phase3-strip">
                {phase3CurrentList.map((symbolId, idx) => {
                  const sym = SYMBOLS.find(s => s.id === symbolId);
                  const revealed = idx < step || status === "won";
                  const isCurrent = idx === step && status === "playing";
                  const cls = ["phase3-cell"];
                  if (revealed) cls.push("done");
                  if (isCurrent) cls.push("current");
                  if (isCurrent && stepSubmitted) cls.push("locked");
                  return (
                    <div key={idx} className={cls.join(" ")}>
                      <span className="phase3-num">{idx + 1}</span>
                      {revealed
                        ? <img src={sym.img} alt={sym.name} />
                        : <span className="qmark">?</span>}
                    </div>
                  );
                })}
              </div>
              <div className="phase3-list-dots">
                {round.lists.map((l, i) => (
                  <div key={i} className={"phase3-dot" + (i < listIdx ? " done" : "") + (i === listIdx ? " current" : "")}>
                    <span className="phase3-dot-label">L{i + 1}</span>
                    <span className="phase3-dot-size">×{l.length}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={"buttons" + (settings.buttonsPosition === "left" || settings.buttonsPosition === "right" ? " buttons-vertical" : "")}>
            {orderedSymbols.map(sym => {
              const expected = isPhase3 ? phase3CurrentList[step] : round.sequence[step];
              const isAutoSlot = !isPhase3 && !myPositions.includes(step);
              const willBeAuto = isAutoSlot && status === "playing" && expected === sym.id;
              const cls = ["symbtn"];
              if (willBeAuto) cls.push("auto");
              if (flashId === sym.id) cls.push("flash");
              return (
                <button
                  key={sym.id}
                  className={cls.join(" ")}
                  onClick={() => {
                    if (status !== "playing" || !isMyTurn || stepSubmitted) return;
                    submit(sym.id, "you");
                  }}
                  disabled={status !== "playing" || !isMyTurn || stepSubmitted}
                  title={sym.name}
                >
                  <img src={sym.img} alt={sym.name} />
                  <span className="lbl">#{sym.id} {sym.name}</span>
                  <span
                    className={"key" + (rebinding === sym.id ? " rebinding" : "")}
                    onClick={(e) => { e.stopPropagation(); setRebinding(sym.id); }}
                    title="Click to rebind, then press the new key (Esc to cancel)"
                  >
                    {rebinding === sym.id ? "press…" : keybinds[sym.id]}
                  </span>
                </button>
              );
            })}
          </div>

          </div>

          <div className={"status" + (status === "won" ? " win" : status === "lost" ? " loss" : "")}>
            {status === "countdown" && <>Get ready…</>}
            {status === "playing" && (
              <>
                {isMyTurn && stepSubmitted
                  ? <>Locked in — waiting for the round timer…</>
                  : isMyTurn
                    ? <>Your move — input position <b>{step + 1}</b> ({(settings.stepTimeoutMs / 1000).toFixed(2)}s).</>
                    : <>Other realm is inputting position <b>{step + 1}</b>…</>}
                <div className="timer-bar">
                  <div
                    key={step}
                    className="timer-fill"
                    style={{ animationDuration: `${settings.stepTimeoutMs}ms` }}
                  />
                </div>
              </>
            )}
            {status === "won"  && <>Combination locked. Nice.</>}
            {status === "lost" && lossReason === "missed" && <>Missed position {step + 1} — out of time.</>}
            {status === "lost" && lossReason === "wrong"  && <>Wrong symbol at position {step + 1}.</>}
            {history.length > 0 && (
              <div className="history">
                {history.map((h, i) =>
                  <span key={i} className="hist-item">
                    {i+1}: {h.missed ? "miss" : `#${h.symbolId}`} ({h.source}{h.reaction != null ? `, ${h.reaction}ms` : ""}){h.ok ? "" : " x"}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="stats">
            <div className="stats-row">
              <span className="stats-title">This round</span>
              <span>{currentReactions.length} click{currentReactions.length === 1 ? "" : "s"}</span>
              <span>avg <b>{avg(currentReactions) ?? "—"}{currentReactions.length ? "ms" : ""}</b></span>
              <span>fastest <b>{fastest(currentReactions) ?? "—"}{currentReactions.length ? "ms" : ""}</b></span>
              <span>slowest <b>{slowest(currentReactions) ?? "—"}{currentReactions.length ? "ms" : ""}</b></span>
            </div>
            <div className="stats-row">
              <span className="stats-title">All rounds</span>
              <span>{roundStats.length} played, {wins} won</span>
              <span>avg <b>{avg(allReactions) ?? "—"}{allReactions.length ? "ms" : ""}</b></span>
              <span>fastest <b>{fastest(allReactions) ?? "—"}{allReactions.length ? "ms" : ""}</b></span>
              <button className="btn" style={{marginLeft: "auto"}} onClick={() => setRoundStats([])}>Reset stats</button>
            </div>
          </div>
        </div>
      )}

      <hr/>
      <div className="footer-small">
        Default keybinds <kbd>1</kbd>–<kbd>5</kbd>. Rebind from <b>⚙ Settings</b> (or click the key chip on any button),
        then press the new key — <kbd>Esc</kbd> to cancel. In edicts modes (Tank / Healer), buttons outlined in green are
        auto-played because they belong to another realm. In Phase 3 every tile is yours.
      </div>
    </div>
  );
}
